import type {
  FieldErrors,
  FieldValues,
  Path,
  UseFormRegister,
  UseFormTrigger,
} from 'react-hook-form';

import InputErrorText from '@/components/InputErrorText/InputErrorText';
import { type Event, trackEvents } from '@/utils/analytics';

import { Radio } from '../Radio/Radio';
import useStyles from './RadioGroup.styles';

type RadioOption = {
  value: string;
  label?: string;
};

type RadioGroupProps<T extends FieldValues> = {
  name: Path<T>;
  options: RadioOption[];
  register: UseFormRegister<T>;
  trigger: UseFormTrigger<T>;
  legend?: string;
  errors?: FieldErrors<T>;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
  isOnDark?: boolean;
  gaEvent?: Event;
  isSmall?: boolean;
  testId?: string;
};

export function RadioGroup<T extends FieldValues>({
  options,
  name,
  register,
  trigger,
  legend,
  errors,
  testId,
  className,
  containerClassName,
  disabled = false,
  gaEvent,
  isOnDark = false,
  isSmall = false,
}: RadioGroupProps<T>) {
  const { classes, cx } = useStyles({ isOnDark });

  const { onChange: hookOnChange, ...radioRegister } = register(name);
  function handleClick(
    e: React.ChangeEvent<HTMLInputElement>,
    label: string | undefined
  ) {
    hookOnChange(e);
    trigger(name);
    if (gaEvent && label) {
      trackEvents({
        ...gaEvent,
        click_text: label,
      });
    }
  }

  return (
    <fieldset
      className={cx(
        classes.fieldset,
        disabled && classes.fieldsetDisabled,
        className,
        containerClassName
      )}
      {...(disabled && { 'aria-disabled': true })}
    >
      {legend && (
        <legend className={cx(isSmall ? classes.legendSmall : classes.legend)}>
          {legend}
        </legend>
      )}
      <div className={cx(classes.radioButtonsContainer, containerClassName)}>
        {options.map((option) => (
          <Radio
            key={option.value}
            {...(testId && { 'data-testid': `${testId}-${option.value}` })}
            {...radioRegister}
            value={option.value}
            id={option.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleClick(e, option.label)
            }
            disabled={disabled}
            isOnDark={isOnDark}
            className={classes.radio}
          >
            <span className={classes.label}>{option.label}</span>
          </Radio>
        ))}
      </div>
      <InputErrorText
        errorMessage={errors?.[name]?.message?.toString()}
        isOnDark={isOnDark}
      />
    </fieldset>
  );
}
